/**
 * Spatial Simon — client entry & UI orchestration.
 *
 * Vanilla TS, no framework: a small reactive-ish loop (mutate `state`, call
 * render()) plus precise, clock-synced timers for playback. The UI is driven by
 * room.phase + the player's role. Classic and Move Simon share every screen;
 * Move only swaps the slot render (icon vs colour flash) and the response
 * capture (camera vs tap) — the engine and protocol are identical.
 */

import './styles.css';
import QRCode from 'qrcode';
import { Net } from './net.js';
import {
  unlockAudio,
  playTone,
  playCelebration,
  playSoftRetry,
  speak,
  vibrate,
} from './audio.js';
import { COLOURS, TONES, MOVES } from '../shared/palette.js';
import type { RoomView, PlayerView, ServerMsg, GameMode } from '../shared/protocol.js';
import type { AssistLevel } from '../shared/tuning.js';
import { shareRecap } from './recap.js';
import type { MoveSession } from './move.js';

/* ----------------------------------------------------------------------- *
 *  Tiny DOM helper
 * ----------------------------------------------------------------------- */
type Props = Record<string, any>;
function el(tag: string, props: Props = {}, ...kids: any[]): HTMLElement {
  const n = document.createElement(tag);
  for (const [k, v] of Object.entries(props)) {
    if (v == null) continue;
    if (k === 'class') n.className = v;
    else if (k === 'style') Object.assign(n.style, v);
    else if (k === 'html') n.innerHTML = v;
    else if (k.startsWith('on') && typeof v === 'function') n.addEventListener(k.slice(2).toLowerCase(), v);
    else n.setAttribute(k, String(v));
  }
  for (const kid of kids.flat()) {
    if (kid == null || kid === false) continue;
    n.append(kid instanceof Node ? kid : document.createTextNode(String(kid)));
  }
  return n;
}

const freqOf = (toneId: string) => TONES.find((t) => t.id === toneId)?.freq ?? 523.25;
const moveOf = (moveId: string) => MOVES.find((m) => m.id === moveId) ?? MOVES[0];

/* ----------------------------------------------------------------------- *
 *  Client state
 * ----------------------------------------------------------------------- */
interface State {
  status: 'home' | 'connecting' | 'inroom' | 'blocked';
  youId: string | null;
  isHost: boolean;
  spectator: boolean;
  room: RoomView | null;
  blockedReason: string;

  // Transient gameplay
  flash: boolean; // my colour is flashing (playback) or I'm being celebrated
  responseActive: boolean; // armed: reproduce the sequence from memory
  myCue: boolean; // high-assist private "your turn" cue (Sophia only)
  warnFlash: number; // >0 briefly drives the red warning flash; the value = strike count
  progress: { remembered: number; total: number } | null; // big-screen progress
  litBead: number | null; // host ribbon (playback only)
  playbackOwners: Record<number, string>; // index -> playerId, for big-screen playback colours
  beadState: Record<number, 'done' | 'miss'>;
  celebrate: boolean;
  moveSession: MoveSession | null;
  moveError: string | null;
  joinCode: string;
}

const state: State = {
  status: 'home',
  youId: null,
  isHost: false,
  spectator: false,
  room: null,
  blockedReason: '',
  flash: false,
  responseActive: false,
  myCue: false,
  warnFlash: 0,
  progress: null,
  litBead: null,
  playbackOwners: {},
  beadState: {},
  celebrate: false,
  moveSession: null,
  moveError: null,
  joinCode: '',
};

const TOKEN_KEY = 'spatial-simon-token';
const net = new Net(
  (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host,
);
const playbackTimers: ReturnType<typeof setTimeout>[] = [];

/* ----------------------------------------------------------------------- *
 *  Bootstrap
 * ----------------------------------------------------------------------- */
const params = new URLSearchParams(location.search);
const urlCode = (params.get('r') || '').toUpperCase();
const wantScreen = params.get('screen') === '1';
state.joinCode = urlCode;

async function boot() {
  net.onMessage(onServer);
  try {
    await net.connect();
    await net.syncClock();
  } catch {
    showToast('Could not reach the game server.');
    return;
  }
  // Try to reclaim a seat (refresh/reconnect, §12).
  const token = localStorage.getItem(TOKEN_KEY);
  if (token) {
    net.send({ t: 'rejoin', token });
  } else if (wantScreen && urlCode) {
    // A TV opening a screen link joins straight as a spectator.
    joinRoom(urlCode, true);
  }
  render();
}

function me(): PlayerView | null {
  return state.room?.players.find((p) => p.id === state.youId) || null;
}

/* ----------------------------------------------------------------------- *
 *  Server message handling
 * ----------------------------------------------------------------------- */
function onServer(msg: ServerMsg) {
  switch (msg.t) {
    case 'welcome':
      state.youId = msg.youId;
      state.isHost = msg.isHost;
      state.spectator = msg.spectator;
      state.room = msg.room;
      state.status = 'inroom';
      localStorage.setItem(TOKEN_KEY, msg.token);
      applyReducedFlash();
      // "This is you": flash + tone once on join (§5).
      if (!msg.spectator) introduceYou();
      render();
      break;

    case 'room':
      state.room = msg.room;
      applyReducedFlash();
      render();
      break;

    case 'countdown':
      render();
      break;

    case 'lobbyClosed':
      state.status = 'blocked';
      state.blockedReason = msg.reason;
      render();
      break;

    case 'gameStart':
      resetRoundTransients();
      render();
      break;

    case 'playback':
      schedulePlayback(msg.slots);
      break;

    case 'responseOpen':
      state.responseActive = true;
      state.myCue = false;
      state.beadState = {};
      state.progress = { remembered: 0, total: state.room?.sequenceLength ?? 0 };
      // Arm: announce the memory challenge once (no order revealed).
      if (!state.spectator) {
        vibrate(20);
        if (me()?.assist === 'high') speak('Your turn to remember');
      }
      render();
      // Move mode: the camera watches the WHOLE response phase, since nobody is
      // told when their turn is — a detected move sends a respond like a tap.
      if (state.room?.mode === 'move' && !state.spectator) setTimeout(() => startMyMove(), 0);
      break;

    case 'yourTurn':
      // Private cue — only a high-assist player (Sophia) ever receives this.
      state.myCue = true;
      vibrate(40);
      { const mine = me(); if (mine) { playTone(freqOf(mine.toneId)); speak('Your turn'); } }
      render();
      break;

    case 'progress':
      state.progress = { remembered: msg.remembered, total: msg.total };
      render();
      break;

    case 'warning':
      onWarning(msg);
      break;

    case 'slot':
      onSlotResult(msg);
      break;

    case 'roundResult':
      onRoundResult(msg);
      break;

    case 'retry':
      showToast("Let's try that one again — together! 💪");
      playSoftRetry();
      break;

    case 'error':
      if (msg.message === '__disconnected__') {
        handleDisconnect();
      } else {
        showToast(msg.message);
      }
      break;
  }
}

function onWarning(msg: Extract<ServerMsg, { t: 'warning' }>) {
  if (msg.playerId !== state.youId || state.spectator) return;
  // Escalating: stronger the more strikes you've stacked up.
  warningFeedback(msg.count, msg.kind);
}

function onSlotResult(msg: Extract<ServerMsg, { t: 'slot' }>) {
  state.beadState[msg.slotIndex] = msg.correct ? 'done' : 'miss';
  if (!state.spectator) {
    if (msg.playerId === state.youId) {
      if (msg.correct) positiveFeedback(); // 'late' warning arrives separately
      state.myCue = false;
    } else if (msg.correct) {
      // Someone else remembered right — a subtle shared tick (rhythm, no reveal).
      neutralTick();
    }
  }
  render();
}

function onRoundResult(msg: Extract<ServerMsg, { t: 'roundResult' }>) {
  stopMyMove();
  state.responseActive = false;
  state.myCue = false;
  if (msg.success) {
    state.celebrate = true;
    render();
    celebrate();
    setTimeout(() => {
      state.celebrate = false;
      render();
    }, 2600);
  }
  render();
}

/* ----------------------------------------------------------------------- *
 *  Playback scheduling (clock-synced, §11)
 * ----------------------------------------------------------------------- */
function schedulePlayback(slots: Extract<ServerMsg, { t: 'playback' }>['slots']) {
  clearPlaybackTimers();
  resetRoundTransients();
  render();

  for (const slot of slots) {
    const localFire = net.toLocal(slot.fireAt);
    const delay = Math.max(0, localFire - Date.now());

    if (!state.spectator && slot.playerId === state.youId) {
      // MY slot: play my tone precisely + flash + haptic (audio travels to my hand).
      playTone(freqOf(me()!.toneId), localFire);
      playbackTimers.push(
        setTimeout(() => {
          state.flash = true;
          vibrate(60);
          render();
          playbackTimers.push(
            setTimeout(() => {
              state.flash = false;
              render();
            }, slot.lightMs),
          );
        }, delay),
      );
    }

    if (state.spectator || state.isHost) {
      // Big screen: remember each bead's owner so playback can colour the
      // travelling sequence (playback is the public demonstration).
      state.playbackOwners[slot.index] = slot.playerId;
      // Big screen: light the travelling bead.
      playbackTimers.push(
        setTimeout(() => {
          state.litBead = slot.index;
          render();
          playbackTimers.push(
            setTimeout(() => {
              if (state.litBead === slot.index) state.litBead = null;
              render();
            }, slot.lightMs),
          );
        }, delay),
      );
    }
  }
}

function clearPlaybackTimers() {
  for (const t of playbackTimers) clearTimeout(t);
  playbackTimers.length = 0;
}

function resetRoundTransients() {
  state.flash = false;
  state.responseActive = false;
  state.myCue = false;
  state.warnFlash = 0;
  state.progress = null;
  state.litBead = null;
  state.playbackOwners = {};
  state.beadState = {};
}

/* ----------------------------------------------------------------------- *
 *  Responding (tap or move)
 * ----------------------------------------------------------------------- */
// Any tap during the response phase sends a "respond"; the SERVER decides if it
// was correct / out-of-turn / late (the client is never told whose turn it is).
function respondNow(via: 'tap' | 'move') {
  if (!state.responseActive || state.spectator) return;
  net.send({ t: 'respond', clientTime: Date.now(), via });
  // Local "I tapped" tick — NOT a correctness signal (server confirms via 'slot').
  const mine = me();
  if (mine) {
    playTone(freqOf(mine.toneId));
    vibrate(30);
  }
}

function positiveFeedback() {
  const mine = me();
  if (!mine) return;
  state.flash = true;
  playTone(freqOf(mine.toneId));
  vibrate([0, 40, 30, 40]);
  render();
  setTimeout(() => {
    state.flash = false;
    render();
  }, 450);
}

// Subtle neutral pulse when ANOTHER player remembers right — shared rhythm,
// without revealing whose turn is next.
function neutralTick() {
  vibrate(12);
}

// Escalating warning feedback. The more strikes stacked, the harsher: longer
// buzz, more red, a lower/harsher tone, and (at higher counts) a spoken nudge.
function warningFeedback(count: number, kind: 'late' | 'wrong' | 'timeout') {
  state.warnFlash = count;
  const intensity = Math.min(count, 5);
  vibrate(Array.from({ length: intensity }, () => [90, 60]).flat());
  // Harsher tone as it stacks (drops in pitch).
  playTone(Math.max(120, 320 - count * 35));
  if (kind === 'wrong' && count >= 2) speak('Wrong turn');
  else if (kind === 'late' && count >= 3) speak('Too slow');
  render();
  setTimeout(() => {
    state.warnFlash = 0;
    render();
  }, 700 + intensity * 120);
}

/* ----------------------------------------------------------------------- *
 *  Move Simon (lazy)
 * ----------------------------------------------------------------------- */
async function startMyMove() {
  stopMyMove();
  state.moveError = null;
  const mine = me();
  if (!mine) return;
  const video = document.getElementById('cam') as HTMLVideoElement | null;
  if (!video) return;
  try {
    const { startMoveDetection } = await import('./move.js');
    state.moveSession = await startMoveDetection(
      video,
      mine.moveId,
      () => respondNow('move'),
      (reason) => {
        state.moveError = reason;
        render(); // falls back to the tap button automatically
      },
    );
  } catch (e: any) {
    state.moveError = e?.message || 'vision unavailable';
    render();
  }
}

function stopMyMove() {
  state.moveSession?.stop();
  state.moveSession = null;
}

/* ----------------------------------------------------------------------- *
 *  Celebrations / feedback (§6)
 * ----------------------------------------------------------------------- */
function celebrate() {
  const mine = me();
  // Each phone plays its own tone → a chord across the room.
  if (mine) playCelebration([freqOf(mine.toneId), freqOf(mine.toneId) * 1.25]);
  else playCelebration(TONES.map((t) => t.freq));
  vibrate([0, 80, 60, 80, 60, 120]);
}

function introduceYou() {
  const mine = me();
  if (!mine) return;
  setTimeout(() => {
    playTone(freqOf(mine.toneId));
    vibrate(50);
    state.flash = true;
    render();
    setTimeout(() => {
      state.flash = false;
      render();
    }, 600);
    speak(`You are ${COLOURS.find((c) => c.id === mine.colour)?.name || ''}`);
  }, 300);
}

/* ----------------------------------------------------------------------- *
 *  Actions
 * ----------------------------------------------------------------------- */
function createRoom(asPlayer: boolean) {
  unlockAudio();
  net.send({ t: 'createRoom', asPlayer, difficulty: savedDifficulty() });
}
function joinRoom(code: string, spectator = false) {
  unlockAudio();
  if (!code) return;
  net.send({ t: 'joinRoom', code: code.toUpperCase(), spectator });
}
const startGame = () => net.send({ t: 'startGame' });
const setMode = (mode: GameMode) => net.send({ t: 'setMode', mode });
const setReducedFlash = (on: boolean) => net.send({ t: 'setReducedFlash', on });
const DIFFICULTY_KEY = 'spatial-simon-difficulty';
function setDifficulty(level: number) {
  // Remember the host's choice on this device for next time.
  try { localStorage.setItem(DIFFICULTY_KEY, String(level)); } catch { /* ignore */ }
  net.send({ t: 'setDifficulty', level });
}
function savedDifficulty(): number | undefined {
  const v = Number(localStorage.getItem(DIFFICULTY_KEY));
  return Number.isInteger(v) && v >= 0 && v <= 4 ? v : undefined;
}
const pauseGame = () => net.send({ t: 'pause' });
const resumeGame = () => net.send({ t: 'resume' });
/** Leave the current room and return to the home screen (drops the saved token
 *  so a refresh doesn't drop you straight back in). */
function leaveToHome() {
  try { net.send({ t: 'leave' }); } catch { /* ignore */ }
  localStorage.removeItem(TOKEN_KEY);
  location.href = location.origin; // clean reload to home, drops ?r=
}
function restartGame() {
  // Friendly confirm so a mis-tap doesn't dump everyone back to the lobby.
  if (confirm('Restart? Everyone goes back to the lobby (no need to re-join).')) {
    net.send({ t: 'restart' });
  }
}
// Host picks the ONE player who gets special privileges (private cue + penalty
// exemption), or nobody. Tapping the current pick clears it.
function selectAssisted(p: PlayerView) {
  net.send({ t: 'setAssisted', playerId: p.assist === 'high' ? null : p.id });
}
const clearAssisted = () => net.send({ t: 'setAssisted', playerId: null });

/* ----------------------------------------------------------------------- *
 *  Reconnect
 * ----------------------------------------------------------------------- */
let reconnecting = false;
async function handleDisconnect() {
  if (reconnecting) return;
  reconnecting = true;
  showToast('Reconnecting…');
  while (!net.connected) {
    try {
      await net.connect();
      await net.syncClock();
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) net.send({ t: 'rejoin', token });
      showToast('Back online');
    } catch {
      await new Promise((r) => setTimeout(r, 1500));
    }
  }
  reconnecting = false;
}

function applyReducedFlash() {
  document.body.classList.toggle('reduced-flash', !!state.room?.reducedFlash);
}

/* ----------------------------------------------------------------------- *
 *  Rendering
 * ----------------------------------------------------------------------- */
const app = () => document.getElementById('app')!;
function mount(node: HTMLElement) {
  const root = app();
  root.replaceChildren(node);
}

function render() {
  if (state.status === 'home') return mount(renderHome());
  if (state.status === 'blocked') return mount(renderBlocked());
  const room = state.room;
  if (!room) return mount(renderConnecting());

  // Spectator / host big screen.
  if (state.spectator) return mount(renderScreen(room));

  // Player screens by phase.
  if (room.phase === 'lobby' || room.phase === 'countdown') return mount(renderLobby(room));
  if (room.phase === 'ended') return mount(renderEnded(room));
  return mount(renderPlaying(room));
}

/* ---- Home ---- */
function renderHome(): HTMLElement {
  const codeInput = el('input', {
    type: 'text',
    maxlength: '4',
    placeholder: 'CODE',
    value: state.joinCode,
    inputmode: 'latin',
    autocapitalize: 'characters',
    oninput: (e: any) => (state.joinCode = e.target.value.toUpperCase()),
  }) as HTMLInputElement;

  return el(
    'div',
    { class: 'screen' },
    el('div', { class: 'icon', style: { fontSize: '4rem' } }, '🎵'),
    el('h1', {}, 'Spatial Simon'),
    el('p', { class: 'muted' }, 'A memory game for the whole room. Each phone is one button.'),
    el(
      'div',
      { class: 'stack' },
      // Primary action — this is how you play.
      el('button', { class: 'primary big', onclick: () => createRoom(true) }, '✨ Start a room'),
      el('div', { class: 'muted' }, 'or join one:'),
      codeInput,
      el('button', { onclick: () => joinRoom(codeInput.value) }, 'Join game'),
      // Secondary, de-emphasised: a TV display for an existing room.
      el(
        'a',
        {
          class: 'muted',
          style: { fontSize: '0.85rem', marginTop: '18px', cursor: 'pointer', textDecoration: 'underline' },
          onclick: () => createRoom(false),
        },
        '📺 TV mode (second screen — players still join from phones)',
      ),
    ),
  );
}

function renderConnecting(): HTMLElement {
  return el('div', { class: 'screen' }, el('h1', {}, 'Connecting…'));
}

function renderBlocked(): HTMLElement {
  return el(
    'div',
    { class: 'screen' },
    el('div', { style: { fontSize: '4rem' } }, '⏳'),
    el('h1', {}, 'Next round!'),
    el('p', { class: 'big' }, state.blockedReason),
    el('p', { class: 'muted' }, "You're in the lobby for the next game."),
  );
}

/* ---- Lobby (player) ---- */
function renderLobby(room: RoomView): HTMLElement {
  const mine = me();
  const counting = room.phase === 'countdown';
  const secs = counting && room.lobbyLocksAt
    ? Math.max(0, Math.ceil((net.toLocal(room.lobbyLocksAt) - Date.now()) / 1000))
    : null;
  if (counting) scheduleTick();

  const joinUrl = `${location.origin}/?r=${room.code}`;
  const screenUrl = `${joinUrl}&screen=1`;

  // The player's own identity, full bleed = "this is you".
  const identity = mine
    ? el(
        'div',
        {
          class: 'card',
          style: {
            background: mine.colourHex,
            color: COLOURS.find((c) => c.id === mine.colour)?.text || '#fff',
            textAlign: 'center',
          },
        },
        el('div', { style: { fontSize: '4rem' } }, mine.icon),
        el('div', { class: 'big' }, 'This is you'),
        room.mode === 'move'
          ? el('div', { class: 'big' }, `Your move: ${moveOf(mine.moveId).icon} ${moveOf(mine.moveId).verb}`)
          : null,
      )
    : null;

  const children: any[] = [identity];

  if (secs != null) {
    children.push(el('div', { class: 'huge' }, String(secs)));
    children.push(el('p', { class: 'big' }, 'Get ready! Game starting…'));
  } else {
    children.push(
      el('div', { class: 'card' },
        el('div', { class: 'muted' }, 'Room code'),
        el('div', { class: 'code' }, room.code),
        qrFor(joinUrl),
        el('div', { class: 'muted', style: { wordBreak: 'break-all', marginTop: '8px' } }, joinUrl),
      ),
    );
  }

  children.push(renderPlayerChips(room, /*interactive host*/ state.isHost));

  if (state.isHost && !counting) {
    children.push(renderHostLobbyControls(room, screenUrl));
  } else if (!counting) {
    children.push(el('p', { class: 'muted' }, 'Waiting for the host to start…'));
  }
  if (!counting) {
    children.push(el('button', { class: 'ghost muted', onclick: leaveToHome }, '← Leave'));
  }

  return el('div', { class: 'screen' }, el('div', { class: 'stack' }, ...children));
}

function renderHostLobbyControls(room: RoomView, screenUrl: string): HTMLElement {
  return el(
    'div',
    { class: 'stack' },
    renderAssistPicker(room),
    el(
      'div',
      { class: 'seg' },
      el(
        'button',
        { class: room.mode === 'classic' ? 'on' : '', onclick: () => setMode('classic') },
        '🎨 Classic',
      ),
      el(
        'button',
        { class: room.mode === 'move' ? 'on' : '', onclick: () => setMode('move') },
        '🤸 Move',
      ),
    ),
    renderDifficultySlider(room),
    el(
      'label',
      { class: 'row muted' },
      el('input', {
        type: 'checkbox',
        checked: room.reducedFlash ? 'checked' : null,
        onchange: (e: any) => setReducedFlash(e.target.checked),
      }),
      ' Reduced flashing',
    ),
    el(
      'button',
      { class: 'primary big', disabled: room.players.length === 0 ? 'true' : null, onclick: startGame },
      '▶️ Start (15s to join)',
    ),
    el('a', { href: screenUrl, target: '_blank', class: 'btn ghost muted' }, '📺 Open big screen'),
  );
}

/** Host picks the ONE player who gets special privileges (private "your turn"
 *  cue + exemption from penalties), or Nobody. Tap a player chip above to choose;
 *  this row shows the choice and offers "Nobody". */
function renderAssistPicker(room: RoomView): HTMLElement {
  const chosen = room.players.find((p) => p.assist === 'high');
  return el(
    'div',
    { class: 'card', style: { textAlign: 'center' } },
    el('div', { class: 'muted' }, 'Who gets extra help? (optional)'),
    el(
      'div',
      { class: 'big', style: { margin: '4px 0' } },
      chosen ? `💛 ${chosen.icon} ${chosen.name}` : '🙅 Nobody',
    ),
    el('div', { class: 'muted', style: { fontSize: '0.8rem' } },
      'Tap a player above to give them the “your turn” cue and no penalties.'),
    chosen
      ? el('button', { class: 'ghost', style: { marginTop: '8px' }, onclick: clearAssisted }, '🙅 Nobody needs help')
      : null,
  );
}

const DIFFICULTY_NAMES = ['Very easy', 'Easy', 'Normal', 'Hard', 'Very hard'];
/** Host difficulty slider: one control for flash speed, sequence growth, and
 *  the tap window. Works in the lobby and live (the host can drag it any time). */
function renderDifficultySlider(room: RoomView): HTMLElement {
  const name = room.difficultyName || DIFFICULTY_NAMES[room.difficulty] || 'Normal';
  return el(
    'div',
    { class: 'card', style: { textAlign: 'center' } },
    el(
      'div',
      { class: 'row', style: { justifyContent: 'space-between', width: '100%' } },
      el('span', { class: 'muted' }, '🐢 Easy'),
      el('b', {}, name),
      el('span', { class: 'muted' }, 'Hard 🔥'),
    ),
    el('input', {
      type: 'range',
      min: '0',
      max: '4',
      step: '1',
      value: String(room.difficulty),
      style: { width: '100%' },
      oninput: (e: any) => setDifficulty(Number(e.target.value)),
    }),
    el('div', { class: 'muted', style: { fontSize: '0.8rem' } },
      'Flash speed · how fast the sequence grows · time to tap'),
  );
}

/* ---- Playing (player) ---- */
function renderPlaying(room: RoomView): HTMLElement {
  const mine = me();
  if (!mine) return renderConnecting();
  const textColour = COLOURS.find((c) => c.id === mine.colour)?.text || '#fff';
  const moveInfo = moveOf(mine.moveId);

  // Wrap the base screen with the paused overlay + host control bar (if host).
  const wrap = (base: HTMLElement) => {
    if (room.paused) base.append(renderPausedOverlay());
    if (state.isHost) base.append(renderHostGameBar(room));
    return base;
  };

  if (state.celebrate) return wrap(renderCelebrate(room));

  // PLAYBACK: my colour flashes when my slot fires; otherwise calm "watch".
  if (room.playPhase === 'playback') {
    if (state.flash) {
      return wrap(el(
        'div',
        { class: 'fullcolour flash', style: { background: mine.colourHex, color: textColour } },
        el('div', { class: 'icon' }, room.mode === 'move' ? moveInfo.icon : mine.icon),
        el('div', { class: 'label' }, 'Watch & listen'),
      ));
    }
    return wrap(renderWatch('Watch & remember the order… 👀👂', room));
  }

  // RESPONSE: every phone is an armed button. You tap from MEMORY — nobody is
  // told whose turn it is. The server judges correct / out-of-turn / late.
  if (state.responseActive) {
    const classes = ['fullcolour', 'tap'];
    if (state.flash) classes.push('flash');
    if (state.myCue) classes.push('precue'); // private cue, high-assist only
    const label = state.myCue
      ? room.mode === 'move' ? `Your turn — your move! ${moveInfo.icon}` : 'Your turn! 👉'
      : room.mode === 'move' ? `Your move when it's your turn ${moveInfo.icon}` : "Tap when it's your turn 🧠";

    const surface = el(
      'div',
      {
        class: classes.join(' '),
        style: { background: mine.colourHex, color: textColour },
        onclick: room.mode !== 'move' ? () => respondNow('tap') : null,
      },
      state.myCue ? el('div', { class: 'ring' }) : null,
      el('div', { class: 'icon' }, room.mode === 'move' ? moveInfo.icon : mine.icon),
      el('div', { class: 'label' }, label),
    );
    if (mine.warnings > 0) surface.append(renderStrikes(mine.warnings));
    if (state.warnFlash > 0) surface.append(renderWarnOverlay(state.warnFlash));

    if (room.mode === 'move') {
      surface.append(el('video', { id: 'cam', class: 'cam', playsinline: 'true', muted: 'true' }));
      surface.append(el(
        'button',
        { class: 'primary', style: { position: 'fixed', bottom: '96px', left: '20px', zIndex: 25 }, onclick: () => respondNow('tap') },
        state.moveError ? '✅ My move (no camera)' : '✅ My move',
      ));
    }
    return wrap(surface);
  }

  // result / retry / between rounds: calm.
  return wrap(renderWatch('…', room));
}

/** Calm full-screen "watch" state with the shared streak. */
function renderWatch(message: string, room: RoomView): HTMLElement {
  return el(
    'div',
    { class: 'watch' },
    el('div', { class: 'eyes' }, '👀'),
    el('div', { class: 'big muted' }, message),
    el('div', { class: 'streak muted' }, 'Together: ', el('b', {}, String(room.streak))),
  );
}

/** Persistent strike counter (top-right) — grows redder as it stacks. */
function renderStrikes(n: number): HTMLElement {
  const heat = Math.min(n, 5) / 5;
  return el(
    'div',
    {
      class: 'strikes',
      style: { background: `rgba(255,${Math.round(120 * (1 - heat))},${Math.round(60 * (1 - heat))},0.92)` },
    },
    `⚠️ ×${n}`,
  );
}

/** Brief full-screen red warning flash; intensity grows with the strike count. */
function renderWarnOverlay(count: number): HTMLElement {
  const heat = Math.min(count, 5);
  return el(
    'div',
    { class: 'warnflash', style: { background: `rgba(220,30,30,${0.35 + heat * 0.1})` } },
    el('div', { style: { fontSize: `${3 + heat * 0.6}rem` } }, '⚠️'),
    el('div', { class: 'big' }, count >= 4 ? 'WATCH OUT!' : count >= 2 ? 'Careful!' : 'Oops!'),
  );
}

/** Full-screen "paused" overlay shown to everyone while the host has paused. */
function renderPausedOverlay(): HTMLElement {
  return el(
    'div',
    {
      class: 'watch',
      style: { background: 'rgba(13,17,23,0.92)', zIndex: '45' },
    },
    el('div', { style: { fontSize: '4rem' } }, '⏸'),
    el('div', { class: 'big' }, 'Paused'),
    el('div', { class: 'muted' }, state.isHost ? 'Tap Resume below when ready' : 'The host paused the game'),
  );
}

/** Host-only floating control bar during a live game: a live difficulty slider
 *  plus Pause/Resume + Restart. */
function renderHostGameBar(room: RoomView): HTMLElement {
  return el(
    'div',
    { class: 'hostbar', style: { zIndex: '60', flexDirection: 'column' } },
    // Live difficulty (drag any time; takes effect from the next sequence).
    el(
      'label',
      { class: 'row', style: { gap: '8px', width: '100%', justifyContent: 'center' } },
      el('span', { class: 'muted' }, '🐢'),
      el('input', {
        type: 'range',
        min: '0',
        max: '4',
        step: '1',
        value: String(room.difficulty),
        style: { flex: '1', maxWidth: '220px' },
        oninput: (e: any) => setDifficulty(Number(e.target.value)),
      }),
      el('span', { class: 'muted' }, '🔥'),
      el('b', { style: { fontSize: '0.85rem', minWidth: '70px' } }, room.difficultyName),
    ),
    el(
      'div',
      { class: 'row' },
      room.paused
        ? el('button', { class: 'primary', onclick: resumeGame }, '▶️ Resume')
        : el('button', { onclick: pauseGame }, '⏸ Pause'),
      el('button', { class: 'ghost', onclick: restartGame }, '🔄 Restart'),
    ),
  );
}

function renderCelebrate(room: RoomView): HTMLElement {
  return el(
    'div',
    { class: 'celebrate' },
    el('div', { class: 'party' }, '🎉'),
    el('div', { class: 'huge', style: { color: 'var(--ok)' } }, String(room.streak)),
    el('div', { class: 'big' }, 'Yes!! Together! 🙌'),
  );
}

function renderEnded(room: RoomView): HTMLElement {
  return el(
    'div',
    { class: 'screen' },
    el('div', { style: { fontSize: '4rem' } }, '🏁'),
    el('h1', {}, `Best streak: ${room.bestStreak}`),
    el('p', { class: 'big' }, 'Back to the lobby…'),
  );
}

/* ---- Big screen / spectator (§9) ---- */
function renderScreen(room: RoomView): HTMLElement {
  const joinUrl = `${location.origin}/?r=${room.code}`;
  if (room.phase === 'lobby' || room.phase === 'countdown') {
    const secs = room.phase === 'countdown' && room.lobbyLocksAt
      ? Math.max(0, Math.ceil((net.toLocal(room.lobbyLocksAt) - Date.now()) / 1000))
      : null;
    if (secs != null) scheduleTick();
    const noPlayers = room.players.length === 0;
    return el(
      'div',
      { class: 'screen' },
      el('h1', {}, 'Spatial Simon'),
      secs != null
        ? el('div', { class: 'huge' }, String(secs))
        : el('div', { class: 'card' },
            el('div', { class: 'muted' }, 'Join on your phone:'),
            el('div', { class: 'code' }, room.code),
            qrFor(joinUrl)),
      // Host can pick assist by tapping chips here too.
      renderPlayerChips(room, state.isHost && secs == null),
      // Host gets real controls on this screen; otherwise just a mode label.
      state.isHost && secs == null
        ? el(
            'div',
            { class: 'stack' },
            el(
              'div',
              { class: 'seg' },
              el('button', { class: room.mode === 'classic' ? 'on' : '', onclick: () => setMode('classic') }, '🎨 Classic'),
              el('button', { class: room.mode === 'move' ? 'on' : '', onclick: () => setMode('move') }, '🤸 Move'),
            ),
            renderAssistPicker(room),
            renderDifficultySlider(room),
            el(
              'button',
              { class: 'primary big', disabled: noPlayers ? 'true' : null, onclick: startGame },
              '▶️ Start (15s to join)',
            ),
            noPlayers
              ? el('p', { class: 'muted' }, 'This is the big-screen view — players join from their phones (scan the code), then Start. To play on THIS device instead, use “Start over”.')
              : null,
            el('button', { class: 'ghost muted', onclick: leaveToHome }, '← Start over'),
          )
        : el('div', { class: 'stack' },
            el('p', { class: 'muted' }, `${room.mode === 'move' ? '🤸 Move' : '🎨 Classic'} mode`),
            el('button', { class: 'ghost muted', onclick: leaveToHome }, '← Leave'),
          ),
    );
  }

  // Live game: during PLAYBACK the ribbon shows the travelling sequence (the
  // public demonstration). During RESPONSE it shows only neutral progress
  // (✓ filled as the room remembers correctly) — never the upcoming order.
  const beads = el(
    'div',
    { class: 'ribbon' },
    ...Array.from({ length: room.sequenceLength }, (_, i) => {
      const colour = beadColour(room, i);
      const lit = room.playPhase === 'playback' && state.litBead === i;
      return el('div', { class: lit ? 'bead lit' : 'bead', style: { background: colour.bg, color: colour.fg } }, colour.icon);
    }),
  );

  const status = room.paused
    ? '⏸ Paused'
    : room.playPhase === 'playback'
      ? 'Watch & remember the order…'
      : state.responseActive && state.progress
        ? `Remembering… ${state.progress.remembered}/${state.progress.total}`
        : ' ';

  return el(
    'div',
    { class: 'screen' },
    state.celebrate
      ? el('div', { class: 'party', style: { fontSize: '5rem' } }, '🎉')
      : el('div', { class: 'streak' }, 'Together streak: ', el('b', {}, String(room.streak))),
    renderPlayerChips(room, false),
    el('div', { class: 'big muted' }, status),
    beads,
    el('button', { class: 'ghost', onclick: () => shareRecap(room) }, '📸 Share our streak'),
  );
}

function beadColour(room: RoomView, index: number) {
  // During playback the lit bead shows its owner's colour (the demonstration).
  if (room.playPhase === 'playback' && state.litBead === index) {
    const owner = room.players.find((p) => p.id === state.playbackOwners[index]);
    if (owner) return { bg: owner.colourHex, fg: '#fff', icon: owner.icon };
  }
  // Response/idle: neutral progress only — no colour reveals the order.
  const done = state.beadState[index];
  if (done === 'done') return { bg: '#1db95455', fg: '#fff', icon: '✓' };
  if (done === 'miss') return { bg: '#ff525255', fg: '#fff', icon: '·' };
  return { bg: '#2b3340', fg: '#9aa4b2', icon: '•' };
}

/* ---- Shared bits ---- */
function renderPlayerChips(room: RoomView, interactive: boolean): HTMLElement {
  return el(
    'div',
    { class: 'players' },
    ...room.players.map((p) => {
      const cls = ['chip'];
      if (!p.connected) cls.push('offline');
      const assistBadge = p.assist === 'high' ? '💛' : p.assist === 'light' ? '🔆' : '';
      return el(
        'div',
        {
          class: cls.join(' '),
          style: { background: p.colourHex },
          onclick: interactive ? () => selectAssisted(p) : null,
          title: interactive ? 'Tap to give this player special help' : undefined,
        },
        assistBadge ? el('div', { class: 'assist' }, assistBadge) : null,
        // Warning strikes accrued this game.
        room.phase === 'playing' && p.warnings > 0
          ? el('div', { class: 'warnbadge' }, `⚠️${p.warnings}`)
          : null,
        el('div', { class: 'pico' }, p.icon),
        el('div', { class: 'pname' }, p.name + (p.isHost ? ' ⭐' : '')),
      );
    }),
  );
}

function qrFor(url: string): HTMLElement {
  const wrap = el('div', { id: 'qr' });
  const canvas = document.createElement('canvas');
  wrap.append(canvas);
  QRCode.toCanvas(canvas, url, { width: 220, margin: 1 }).catch(() => {
    wrap.replaceChildren(el('div', { class: 'muted' }, url));
  });
  return wrap;
}

/* ----------------------------------------------------------------------- *
 *  Toast + countdown ticking
 * ----------------------------------------------------------------------- */
let toastTimer: ReturnType<typeof setTimeout> | null = null;
function showToast(text: string) {
  const t = document.getElementById('toast')!;
  t.textContent = text;
  t.classList.add('show');
  if (toastTimer) clearTimeout(toastTimer);
  toastTimer = setTimeout(() => t.classList.remove('show'), 2600);
}

// Re-render once per ~250ms while a countdown is visible (cheap, only in lobby).
let ticking = false;
function scheduleTick() {
  if (ticking) return;
  ticking = true;
  const tick = () => {
    if (state.room?.phase === 'countdown') {
      render();
      setTimeout(tick, 250);
    } else {
      ticking = false;
    }
  };
  setTimeout(tick, 250);
}

boot();
